import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import videoCreatorApi from '../../utils/videoCreatorApi';
import HelpVideoPlayerDialog from './HelpVideoPlayerDialog';

const TAB_PALETTE = [
  '#1565c0',
  '#2e7d32',
  '#6a1b9a',
  '#c62828',
  '#00838f',
  '#ef6c00',
  '#4527a0',
  '#558b2f',
  '#ad1457',
  '#37474f',
  '#0277bd',
  '#5d4037',
];

function soft(hex) {
  return `${hex}22`;
}

/**
 * Employee-facing ERP4C Product Help — module tabs + playable videos with transcript.
 */
export default function HelpProduct() {
  const [modules, setModules] = useState([]);
  const [tab, setTab] = useState(0);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [err, setErr] = useState('');
  const [play, setPlay] = useState(null);
  const [credited, setCredited] = useState('');

  const loadModules = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await videoCreatorApi.get('/v1/modules');
      setModules(r.data?.data || []);
    } catch (e) {
      setErr(e.response?.data?.message || e.message || 'Failed to load help modules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadModules();
  }, [loadModules]);

  const active = modules[tab] || null;

  useEffect(() => {
    if (!active?.id) {
      setVideos([]);
      return;
    }
    let cancelled = false;
    setLoadingVideos(true);
    videoCreatorApi
      .get(`/v1/modules/${active.id}/videos`)
      .then((r) => {
        if (cancelled) return;
        const list = (r.data?.data?.videos || []).filter((v) => v.source === 'generated' && v.url);
        setVideos(list);
      })
      .catch((e) => {
        if (!cancelled) setErr(e.response?.data?.message || e.message);
      })
      .finally(() => {
        if (!cancelled) setLoadingVideos(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active?.id]);

  return (
    <Box sx={{ p: 2, width: '100%', maxWidth: '100%' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        ERP4C Product Help
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Watch product tutorials for each module. Narration text appears under the video. Minutes you watch are credited
        to your activity log.
      </Typography>

      {credited && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCredited('')}>
          {credited}
        </Alert>
      )}
      {err && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>
          {err}
        </Alert>
      )}
      {loading && <CircularProgress size={28} />}

      {!loading && modules.length > 0 && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
          <Tabs
            value={Math.min(tab, modules.length - 1)}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              width: '100%',
              minHeight: 48,
              '& .MuiTabs-flexContainer': { width: '100%' },
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTab-root': {
                fontWeight: 700,
                minHeight: 48,
                flex: '1 0 auto',
                minWidth: 140,
                maxWidth: 'none',
                px: 2,
              },
              '& .Mui-selected': { color: '#fff !important' },
            }}
          >
            {modules.map((m, i) => {
              const c = TAB_PALETTE[i % TAB_PALETTE.length];
              const selected = tab === i;
              return (
                <Tab
                  key={m.id}
                  label={m.title}
                  sx={{
                    bgcolor: selected ? c : soft(c),
                    color: selected ? '#fff' : c,
                  }}
                />
              );
            })}
          </Tabs>

          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">{active?.title}</Typography>
              {active?.latest_version && <Chip size="small" label={active.latest_version} />}
              {!active?.can_auto_generate && (
                <Chip size="small" variant="outlined" label="Recorder coming soon" />
              )}
            </Stack>

            {loadingVideos && <CircularProgress size={24} />}
            {!loadingVideos && videos.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No generated videos for this module yet. An administrator can create them under Administration → Help
                Setup.
              </Typography>
            )}
            <Stack spacing={1.5}>
              {videos.map((v) => (
                <Paper key={v.id || v.version} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
                    <Box>
                      <Typography fontWeight={700}>{v.title || `${active.title} ${v.version}`}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {v.duration_label || ''}
                        {v.created_at ? ` · ${new Date(v.created_at).toLocaleString()}` : ''}
                      </Typography>
                      {v.description && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {v.description}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="contained"
                      onClick={() =>
                        setPlay({ video: v, moduleId: active.id, moduleTitle: active.title })
                      }
                    >
                      Play
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>
        </Paper>
      )}

      {play && (
        <HelpVideoPlayerDialog
          open
          video={play.video}
          moduleId={play.moduleId}
          moduleTitle={play.moduleTitle}
          onClose={({ minutes } = {}) => {
            setPlay(null);
            if (minutes >= 1) {
              setCredited(`Credited ${minutes} minute(s) of help-video watch time to your activity.`);
            }
          }}
        />
      )}
    </Box>
  );
}
